import type { NationalDataset } from '../../gen/ejot/roofing/v1/types';
import { Field, FieldRow, SelectField } from '../../shared/components';
import { countryDisplayName } from './countryLabels';

export const USER_DEFINED_WIND_OPTION = '__USER_DEFINED__';

export interface NationalWindZoneSelection {
  countryCode: string;
  datasetId: string;
  zoneId: string;
  userDefined: boolean;
}

interface NationalWindZoneSelectorProps {
  datasets: readonly NationalDataset[];
  selection: NationalWindZoneSelection;
  userDefinedValue?: number;
  onChange: (selection: NationalWindZoneSelection) => void;
  onUserDefinedValueChange: (value: string) => void;
}

/**
 * Presents the configured country/zone wind-speed table supplied by the
 * backend. Selecting a speed carries its dataset and zone identifiers to the
 * calculation request without exposing source metadata as a separate user
 * input. User Defined remains available for every advertised country.
 */
export function NationalWindZoneSelector({
  datasets,
  selection,
  userDefinedValue,
  onChange,
  onUserDefinedValueChange,
}: NationalWindZoneSelectorProps) {
  const countryCodes = Array.from(new Set(
    datasets.map((dataset) => dataset.country_code ?? '').filter((code) => code !== ''),
  )).sort((a, b) => countryDisplayName(a).localeCompare(countryDisplayName(b)));
  const countryOptions = countryCodes.map((code) => ({
    value: code,
    label: countryDisplayName(code),
  }));

  const countryDatasets = selection.countryCode === ''
    ? []
    : datasets.filter((dataset) => dataset.country_code === selection.countryCode);

  const sourceSpeedOptions = countryDatasets.flatMap((dataset) =>
    (dataset.wind_zones ?? []).flatMap((zone) => {
      if (!dataset.id || !zone.id || zone.base_wind_velocity?.value === undefined) return [];
      return [{
        value: sourceOptionValue(dataset.id, zone.id),
        label: `${formatWindSpeed(zone.base_wind_velocity.value, zone.base_wind_velocity.display_precision)} m/s — ${zone.display_name ?? zone.id}`,
        datasetId: dataset.id,
        zoneId: zone.id,
        sortKey: zone.base_wind_velocity.value,
      }];
    }),
  ).sort((a, b) => a.sortKey - b.sortKey || a.label.localeCompare(b.label));

  const selectedSource = sourceSpeedOptions.find(
    (option) => option.datasetId === selection.datasetId && option.zoneId === selection.zoneId,
  );
  const selectedDataset = countryDatasets.find((dataset) => dataset.id === selection.datasetId);
  const selectedZone = selectedDataset?.wind_zones?.find((zone) => zone.id === selection.zoneId);
  const selectedValue = selection.userDefined
    ? USER_DEFINED_WIND_OPTION
    : selectedSource?.value ?? '';

  const windSpeedOptions = selection.countryCode === ''
    ? []
    : [
      ...sourceSpeedOptions.map(({ value, label }) => ({ value, label })),
      { value: USER_DEFINED_WIND_OPTION, label: 'User Defined' },
    ];

  return (
    <>
      <FieldRow>
        <SelectField
          label="Country"
          value={selection.countryCode}
          options={countryOptions}
          placeholder="Select country"
          onChange={countryOptions.length > 0
            ? (countryCode) => onChange({ countryCode, datasetId: '', zoneId: '', userDefined: false })
            : undefined}
        />
        <SelectField
          label="Base wind speed"
          value={selectedValue}
          options={windSpeedOptions}
          placeholder={selection.countryCode ? 'Select wind speed' : 'Select country first'}
          onChange={selection.countryCode
            ? (value) => {
              if (value === USER_DEFINED_WIND_OPTION) {
                onChange({
                  countryCode: selection.countryCode,
                  datasetId: '',
                  zoneId: '',
                  userDefined: true,
                });
                return;
              }
              const option = sourceSpeedOptions.find((candidate) => candidate.value === value);
              if (!option) return;
              onChange({
                countryCode: selection.countryCode,
                datasetId: option.datasetId,
                zoneId: option.zoneId,
                userDefined: false,
              });
            }
            : undefined}
        />
      </FieldRow>

      {selection.userDefined && selection.countryCode && (
        <Field
          label="User defined base wind speed"
          symbol="v_b,0"
          unit="m/s"
          type="number"
          value={userDefinedValue === undefined ? '' : String(userDefinedValue)}
          help={`Manual value for ${countryDisplayName(selection.countryCode)}.`}
          onChange={onUserDefinedValueChange}
        />
      )}

      {!selection.userDefined && selectedZone && (
        <FieldRow>
          <Field
            label="Wind zone"
            value={selectedZone.display_name ?? selectedZone.id ?? ''}
            derived
          />
          <Field
            label="National dataset"
            value={selectedDataset?.display_name ?? selectedDataset?.id ?? ''}
            derived
          />
        </FieldRow>
      )}

      {datasets.length === 0 && (
        <p className="field-note field-note-blocking">
          No national wind-zone table is available.
        </p>
      )}
    </>
  );
}

function sourceOptionValue(datasetId: string, zoneId: string): string {
  return `${datasetId}::${zoneId}`;
}

function formatWindSpeed(value: number, precision: number | undefined): string {
  const digits = Math.max(0, Math.min(3, precision ?? 2));
  return value.toFixed(digits);
}
